import { stylesheet } from 'astroturf';
import styled from 'astroturf/react';
import Svg from 'src/components/Svg';

const SelectBox = styled('label')`
  position: relative;
  display: flex;
  width: 100%;
  margin-bottom: 15px;

  & + & {
    border-top: 0;
  }

  &.invisible {
    border: 0;

    div[class*='SelectBoxLabel'] {
      border: 0;
      padding-left: 0;
    }

    div[class*='react-select__control'] {
      background-color: var(--transparent) !important;
    }
  }
`;

const SelectBoxLabel = styled('div')`
  font-size: var(--ph-text-sm);
  display: flex;
  justify-content: space-between;
  flex: 0 0 30%;
  line-height: 1.54;
  align-items: center;
  color: var(--ph-gray-500);
  cursor: pointer;
`;

const SelectBoxContainer = styled('div')`
  width: 100%;
  height: 100%;

  &.error {
    box-shadow: 0 0 0 1px var(--ph-red-500);
    border-radius: var(--ph-border-radius-sm);
  }

  &.flash {
    -webkit-animation: fadeinout 2s linear forwards;
    animation: fadeinout 2s linear forwards;

    @-webkit-keyframes fadeinout {
      50% { box-shadow: 0 0 0 2px var(--ph-green-900); }
    }

    @keyframes fadeinout {
      50% { box-shadow: 0 0 0 2px var(--ph-green-900); }
    }
  }
`;

const SelectBoxRequired = styled('span')`
  position: relative;
  top: 2px;
  color: var(--ph-red-500);
`;

const Stylesheets = stylesheet`
  .react-select {
    position: relative;
  }

  .react-select__control {
    background-color: var(--white) !important;
    min-height: 30px !important;
    height: auto !important;
    border: 1px solid var(--ph-gray-100) !important;
    border-radius: var(--ph-border-radius-sm) !important;
    cursor: pointer !important;

    &--is-focused {
      box-shadow: var(--ph-box-shadow-focus) !important;
    }
  }

  .react-select__value-container {
    font-size: var(--ph-text-sm) !important;
    height: 100% !important;
    padding-left: 5px !important;
    padding-right: 5px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .react-select__single-value {
    margin: 0 !important;
    color: var(--black) !important;
  }

  .react-select__indicator-separator {
    display: none !important;
  }

  .react-select__multi-value {
    height: auto !important;
    padding: 3px 0 3px 10px !important;
    border-radius: var(--ph-border-radius-sm) !important;
    border: 0 !important;
    background-color: var(--ph-light-blue-500) !important;
    justify-content: center;
    align-items: center;
  }

  .react-select__multi-value__label {
    font-size: var(--ph-text-xs) !important;
    color: var(--white) !important;
    padding: 0 !important;
    max-width: 200px !important;
    white-space: normal !important;
  }

  .react-select__multi-value__remove {
    padding: 0 5px !important;
    color: var(--white) !important;
    cursor: pointer;

    &:hover {
      background-color: var(--transparent) !important;
    }
  }

  .react-select__menu-notice--no-options {
    font-size: var(--ph-text-sm);
    font-weight: normal;
    font-stretch: normal;
    line-height: 1.25;
    letter-spacing: normal;
    color: var(--ph-gray-500) !important;
    text-align: left !important;
  }

  .react-select__menu-notice--loading div {
    justify-content: left;
  }

  .react-select__indicator {
    padding-right: 5px !important;
    padding-top: 4px !important;
    padding-bottom: 4px !important;
  }

  .react-select__menu {
    background-color: var(--white) !important;
    border-radius: var(--ph-border-radius-sm) !important;
    box-shadow: var(--ph-box-shadow-sm) !important;
    margin-bottom: 1px !important;
    margin-top: 1px !important;
    left: -1px;
    box-sizing: content-box !important;
    z-index: 2 !important;
  }

  .react-select__menu-list {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .react-select__option {
    cursor: pointer !important;
    transition: var(--ph-background-transition);
    font-size: var(--ph-text-sm) !important;
    padding: 10px !important;
    color: var(--black) !important;
    word-break: break-word;
  }

  .react-select__option--is-focused {
    background-color: rgba(var(--ph-gray-rgb-50), 0.5) !important;
  }

  .react-select__option--is-selected {
    background-color: rgba(var(--ph-gray-rgb-50), 0.8) !important;
  }

  .react-select--is-disabled .react-select__control {
    cursor: not-allowed !important;
    background-color: var(--ph-gray-50) !important;
    border: 1px solid var(--ph-gray-100) !important;
  }

  .react-select--is-disabled .react-select__single-value {
    cursor: not-allowed !important;
    color: var(--ph-gray-300) !important;
    * {
      color: var(--ph-gray-300) !important;
    }
  }

  .react-select__option--is-disabled {
    opacity: 0.5;
    text-decoration: line-through;
  }
`;

const SelectBoxError = styled('div').attrs(
  ({ errorShowOnTop, ...props }) => {
    return {
      ...props,
      style: {
        top: `${errorShowOnTop ? '-6px' : '50%'}`,
        fontSize: `${errorShowOnTop ? '1.2rem' : '14px'}`
      },
    };
  })`
  font-size: var(--ph-text-sm);
  color: var(--ph-red-500);
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translate(0%, -50%);
  text-align: right;
  pointer-events: none;
`;

const QuestionTypeIcon = styled(Svg)`
  width: 25px;
  margin-right: 10px;
  vertical-align: middle;
`;

const OptionWithSubTextContainer = styled('div')`
  display: flex;
`;

const OptionSubText = styled('span')`
  color: var(--ph-gray-200);
  font-size: var(--ph-text-sm);
`;

const OptionExpander = styled('span').attrs(({ optionWidth, ...props }) => (
  {
    ...props,
    style: {
      left: `${optionWidth - 60}px`,
    },
  }
))`
  display: inline-flex;
  position: absolute;
  &:hover {
    > span[class*="OptionExpanderText__"] {
      color: #29a9e0;
    }

    > div[class*="OptionExpanderIcon___"]:before {
      border-color: #29a9e0;
    }
  }
`;

const OptionExpanderText = styled('span')`
  color: var(--ph-gray-200);
  font-size: var(--ph-text-sm);
`;

const OptionExpanderIcon = styled('div')`
  top: 1px;
  right: -3px;
  cursor: pointer;
  z-index: 100;
  position: absolute;
  width: 10px;

  &::before {
    content: '';
    border: solid #dbdbdb;
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: rotate(-45deg);
    position: absolute;
    left: 8px;
    top: 9px;
  }

  &:hover {
    &::before {
      border-color: #29a9e0;
    }
  }

  &.expanded {
    transform: rotate(90deg);
    right: -25px;
    top: 6px;
    &::before {
      left: 8px;
      top: 9px;
    }
  }
`;

export default {
  SelectBox,
  SelectBoxLabel,
  SelectBoxRequired,
  SelectBoxContainer,
  Stylesheets,
  SelectBoxError,
  QuestionTypeIcon,
  OptionWithSubTextContainer,
  OptionSubText,
  OptionExpander,
  OptionExpanderText,
  OptionExpanderIcon,
};
